import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, Briefcase, MapPin, Calendar, X } from "lucide-react";
import collegeLogo from "../assets/mru-logo.png";
import oasis from "../assets/oasis.png";
import CN from "../assets/CN_internship (1).png";
import CN2 from "../assets/CN_internship (2).png";

const Skills = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const skillCategories = [
    {
      title: "Languages",
      items: ["Java", "JavaScript", "SQL", "HTML", "CSS"],
      icon: "💻",
    },
    {
      title: "Frameworks & Libraries",
      items: ["ReactJS", "NextJS", "NodeJS", "ExpressJS", "Tailwind CSS"],
      icon: "⚛️",
    },
    {
      title: "Tools & Technologies",
      items: ["MongoDB", "VS Code", "IntelliJ IDEA", "Git", "GitHub"],
      icon: "🛠️",
    },
    {
      title: "DevOps",
      items: ["Jenkins", "Docker", "Maven", "CI/CD Pipelines"],
      icon: "🚀",
    },
  ];

  const experiences = [
    {
      title: "Web Development Intern",
      company: "Oasis Infobyte",
      description:
        "Gained hands-on experience in building responsive and visually appealing web interfaces using HTML, CSS, and JavaScript. Developed a strong understanding of front-end design principles, user experience, and modern UI practices.",
      certificate:
        "https://drive.google.com/file/d/14YkNE1fOX-hKkPs6TnMjPLAKM7zhBuUY/view?usp=drive_link",
      image: [oasis],
    },
    {
      title: "Teaching Assistant",
      company: "Coding Ninjas",
      description:
        "Helped students understand concepts and guided them in solving specific problems. Enhanced communication, problem-solving, and mentoring abilities through doubt-clearing sessions and individual support.",
      certificate:
        "https://drive.google.com/drive/folders/1vL_zNuMx2xgKrBwh2dHnN172us28Swo3?usp=drive_link",
      image: [CN, CN2],
    },
  ];

  return (
    <section id="skills" className="min-h-screen py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        {/* Skills Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Skills & Expertise</span>
          </h2>
          <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full"></div>
        </motion.div>

        {/* Skill Categories */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {skillCategories.map((category, index) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="glass rounded-xl p-6 hover:scale-105 transition-transform duration-300"
            >
              <div className="text-4xl mb-4">{category.icon}</div>
              <h3 className="text-xl font-bold text-gray-200 mb-4">{category.title}</h3>
              <ul className="space-y-2">
                {category.items.map((item, idx) => (
                  <li key={idx} className="text-gray-300 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Education Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Education</span>
          </h2>
          <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full"></div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass rounded-2xl p-8 mb-16 max-w-4xl mx-auto"
        >
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="glass rounded-xl p-4">
              <img src={collegeLogo} alt="MRU Logo" className="w-20 h-20 object-contain" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="text-gray-300 w-6 h-6" />
                <h3 className="text-2xl font-bold text-gray-200">Manav Rachna University</h3>
              </div>
              <p className="text-gray-200 font-semibold mb-2">
                B.Tech in Computer Science and Engineering
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-300 mb-4">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  2022 - Present
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  Faridabad
                </span>
              </div>
              <p className="text-gray-300 mb-3">
                <span className="font-semibold text-gray-200">CGPA:</span> 7.7
              </p>
              <p className="text-gray-300 leading-relaxed">
                Specialization in{" "}
                <span className="text-gray-100 font-semibold">
                  Cloud DevOps and Full-Stack Development
                </span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Experience Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Experience</span>
          </h2>
          <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="group relative glass rounded-2xl p-6 hover:scale-105 transition-transform duration-300 overflow-hidden"
            >
              <div className="flex items-center gap-3 mb-4">
                <Briefcase className="text-gray-300 w-6 h-6" />
                <div>
                  <h3 className="text-xl font-bold text-gray-200">{exp.title}</h3>
                  <p className="text-gray-300 italic">{exp.company}</p>
                </div>
              </div>

              <p className="text-gray-300 mb-4 leading-relaxed">{exp.description}</p>

              <a
                href={exp.certificate}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-300 hover:text-gray-100 transition-colors text-sm font-semibold"
              >
                View Certificate
                <span>→</span>
              </a>

              {/* 📸 Snapshot Thumbnails */}
              {exp.image && (
                <div className="absolute bottom-4 right-4 flex gap-2">
                  {(Array.isArray(exp.image) ? exp.image : [exp.image]).map((imgSrc, idx) => (
                    <motion.div
                      key={idx}
                      whileHover={{ scale: 1.1 }}
                      className="w-16 h-12 rounded-lg overflow-hidden shadow-md border border-gray-700 cursor-pointer"
                      onClick={() => setSelectedImage(imgSrc)}
                    >
                      <img
                        src={imgSrc}
                        alt={`${exp.company} snapshot ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* 🪟 Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.3 }}
              className="relative max-w-3xl w-full p-4"
            >
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-300"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={selectedImage}
                alt="Expanded view"
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Skills;
